/**
 * Provenance-aware projection from `/api/dex/records` into the per-species
 * detail view-model the Pokémon detail modal renders.
 *
 * Goal: every field shown in the modal is either
 *   - sourced from a real ingested record (scan / manual / csv import), or
 *   - explicitly marked unknown / pending — never a deterministic mock.
 *
 * The detail modal used to call `mockEncounterFor(id)` and derive HP/ATK/DEF
 * from `(id * 7) % 80`-style expressions. That produced confident-looking
 * values for any species, including ones the trainer has never scanned, and
 * for users with legacy seed/CSV imports it also persisted old field-
 * research-shaped metadata that no longer matched the truth.
 *
 * The helpers in this module are pure: no DOM, no I/O, no React. Importable
 * from a `tsx` test runner.
 */

export interface PokemonRecordLike {
  speciesId?: number | null;
  isShiny?: boolean | null;
  isLucky?: boolean | null;
  isShadow?: boolean | null;
  isPurified?: boolean | null;
  ivAtk?: number | null;
  ivDef?: number | null;
  ivSta?: number | null;
  cp?: number | null;
  hp?: number | null;
  nickname?: string | null;
  catchLocation?: string | null;
  catchDate?: number | string | Date | null;
  captureMethod?: string | null;
  tradePartner?: string | null;
  source?: string | null;
  sourceScreen?: string | null;
  createdAt?: number | string | Date | null;
  updatedAt?: number | string | Date | null;
  deletedAt?: number | string | Date | null;
  hiddenAt?: number | string | Date | null;
}

/**
 * Sources that come from a real on-device or screenshot OCR scan. These
 * carry trustworthy stats (CP/HP/IV) when present.
 */
const SCAN_SOURCES = new Set([
  "box_scan",
  "detail_ocr",
  "pokegenie_list",
  "live_scan",
  "pokedex_scan",
  "journal_scan",
  "video_frame",
  "profile_ocr",
]);

const MANUAL_SOURCES = new Set(["manual_catch", "manual"]);
const CSV_SOURCES = new Set(["csv_import"]);

export type ProvenanceTier = "scan" | "manual" | "csv_import" | "unknown";

function classifyProvenance(source: string | null | undefined): ProvenanceTier {
  if (!source) return "unknown";
  if (SCAN_SOURCES.has(source)) return "scan";
  if (MANUAL_SOURCES.has(source)) return "manual";
  if (CSV_SOURCES.has(source)) return "csv_import";
  return "unknown";
}

function isVisible(r: PokemonRecordLike): boolean {
  if (r.deletedAt != null) return false;
  if (r.hiddenAt != null) return false;
  return true;
}

function toMillis(v: number | string | Date | null | undefined): number {
  if (v == null) return 0;
  if (v instanceof Date) return v.getTime();
  if (typeof v === "number") return Number.isFinite(v) ? v : 0;
  if (typeof v === "string") {
    const n = Date.parse(v);
    return Number.isFinite(n) ? n : 0;
  }
  return 0;
}

/**
 * Score a record so we can pick the "most authoritative" one when multiple
 * records exist for a species. Scan-sourced rows beat manual beat CSV beat
 * unknown; within a tier, the most recently updated wins. Hidden/deleted
 * rows are excluded upstream.
 */
function scoreRecord(r: PokemonRecordLike): number {
  const tierWeight: Record<ProvenanceTier, number> = {
    scan: 4,
    manual: 3,
    csv_import: 2,
    unknown: 1,
  };
  const tier = classifyProvenance(r.source ?? null);
  const ts = Math.max(toMillis(r.updatedAt), toMillis(r.createdAt));
  // Bucket the score so tier always dominates the timestamp comparison.
  // (Math.floor(ts / 1000) keeps the absolute number well below the 1e15
  // safe-integer ceiling and the tier weight is multiplied by something
  // bigger than any plausible second-resolution timestamp.)
  return tierWeight[tier] * 1e15 + Math.floor(ts / 1000);
}

/**
 * Pick the most authoritative visible record for a species, or null when
 * the trainer has no usable record for it.
 */
export function selectRecordForSpecies(
  records: ReadonlyArray<PokemonRecordLike> | null | undefined,
  speciesId: number,
): PokemonRecordLike | null {
  if (!records || records.length === 0) return null;
  let best: PokemonRecordLike | null = null;
  let bestScore = -1;
  for (const r of records) {
    if (!isVisible(r)) continue;
    if (typeof r.speciesId !== "number" || r.speciesId !== speciesId) continue;
    const s = scoreRecord(r);
    if (s > bestScore) {
      bestScore = s;
      best = r;
    }
  }
  return best;
}

export interface NumericStat {
  value: number | null;
  /** True only when the value was sourced from a scan-tier record. */
  verified: boolean;
}

export interface IvTriple {
  atk: number | null;
  def: number | null;
  sta: number | null;
  /** True only when *all three* IVs are present and scan-verified. */
  verified: boolean;
}

export interface FirstCaughtFact {
  /** ISO yyyy-mm-dd or null when no scan-derived date is available. */
  iso: string | null;
  verified: boolean;
}

export interface DetailViewModel {
  speciesId: number;
  /** True when at least one visible record exists for this species. */
  hasRecord: boolean;
  provenance: ProvenanceTier;
  caught: boolean;
  shiny: boolean | null;
  lucky: boolean | null;
  shadow: boolean | null;
  hp: NumericStat;
  cp: NumericStat;
  iv: IvTriple;
  firstCaught: FirstCaughtFact;
  catchLocation: { value: string | null; verified: boolean };
  catchSource: { value: string | null; verified: boolean };
  nickname: { value: string | null; verified: boolean };
}

function dateToIso(v: number | string | Date | null | undefined): string | null {
  const ms = toMillis(v);
  if (!ms) return null;
  const d = new Date(ms);
  if (Number.isNaN(d.getTime())) return null;
  const y = d.getUTCFullYear();
  const m = String(d.getUTCMonth() + 1).padStart(2, "0");
  const day = String(d.getUTCDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

const SCAN_TIERS: ReadonlySet<ProvenanceTier> = new Set<ProvenanceTier>(["scan"]);

/**
 * Build the view-model the detail modal renders. The contract:
 *
 *   - If there is no visible record for the species, every field is
 *     blank/unknown and `caught === false`. The modal must surface this
 *     as "Not yet caught" rather than mocking values.
 *
 *   - If the best record is scan-tier, stats that are present in the row
 *     are marked `verified: true`. Absent fields stay `null`.
 *
 *   - If the best record is manual/CSV/unknown, the row's flags
 *     (shiny/lucky/caught) are still trusted because they are user-asserted,
 *     but the numeric stats (HP/CP/IVs) and encounter metadata are NOT
 *     marked verified — those came from a CSV column or hand entry and may
 *     not match what the trainer sees in-game today. The modal renders
 *     those as "—" / "Unknown" / "Pending scan" rather than as the row's
 *     stored value to avoid showing seed/legacy data as confirmed.
 */
export function buildDetailViewModel(
  records: ReadonlyArray<PokemonRecordLike> | null | undefined,
  speciesId: number,
): DetailViewModel {
  const best = selectRecordForSpecies(records, speciesId);
  if (!best) {
    return {
      speciesId,
      hasRecord: false,
      provenance: "unknown",
      caught: false,
      shiny: null,
      lucky: null,
      shadow: null,
      hp: { value: null, verified: false },
      cp: { value: null, verified: false },
      iv: { atk: null, def: null, sta: null, verified: false },
      firstCaught: { iso: null, verified: false },
      catchLocation: { value: null, verified: false },
      catchSource: { value: null, verified: false },
      nickname: { value: null, verified: false },
    };
  }

  const provenance = classifyProvenance(best.source ?? null);
  const isScan = SCAN_TIERS.has(provenance);

  const hp = typeof best.hp === "number" && Number.isFinite(best.hp)
    ? best.hp
    : null;
  const cp = typeof best.cp === "number" && Number.isFinite(best.cp)
    ? best.cp
    : null;

  const atk = typeof best.ivAtk === "number" && Number.isFinite(best.ivAtk)
    ? best.ivAtk
    : null;
  const def = typeof best.ivDef === "number" && Number.isFinite(best.ivDef)
    ? best.ivDef
    : null;
  const sta = typeof best.ivSta === "number" && Number.isFinite(best.ivSta)
    ? best.ivSta
    : null;
  const ivVerified = isScan && atk !== null && def !== null && sta !== null;

  const firstIso = dateToIso(best.catchDate);
  const captureSource = typeof best.captureMethod === "string" && best.captureMethod.length > 0
    ? best.captureMethod
    : null;
  const location = typeof best.catchLocation === "string" && best.catchLocation.length > 0
    ? best.catchLocation
    : null;
  const nickname = typeof best.nickname === "string" && best.nickname.length > 0
    ? best.nickname
    : null;

  return {
    speciesId,
    hasRecord: true,
    provenance,
    caught: true,
    shiny: !!best.isShiny,
    lucky: !!best.isLucky,
    shadow: !!best.isShadow,
    hp: { value: isScan ? hp : null, verified: isScan && hp !== null },
    cp: { value: isScan ? cp : null, verified: isScan && cp !== null },
    iv: {
      atk: isScan ? atk : null,
      def: isScan ? def : null,
      sta: isScan ? sta : null,
      verified: ivVerified,
    },
    firstCaught: {
      iso: isScan ? firstIso : null,
      verified: isScan && firstIso !== null,
    },
    catchLocation: {
      value: isScan ? location : null,
      verified: isScan && location !== null,
    },
    catchSource: {
      value: isScan ? captureSource : null,
      verified: isScan && captureSource !== null,
    },
    nickname: {
      // Nickname is user-asserted in every path (manual + CSV included)
      // so we surface it even on non-scan tiers, but only mark as
      // verified when it came from a scan path.
      value: nickname,
      verified: isScan && nickname !== null,
    },
  };
}

/**
 * Format an IV triple for display. Returns null when not all three IVs
 * are present (so the modal can show "Unknown" instead of a partial
 * value like "15/—/15").
 */
export function formatIvTriple(iv: IvTriple): string | null {
  if (iv.atk === null || iv.def === null || iv.sta === null) return null;
  return `${iv.atk}/${iv.def}/${iv.sta}`;
}

export function formatIvPercent(iv: IvTriple): string | null {
  if (iv.atk === null || iv.def === null || iv.sta === null) return null;
  const pct = ((iv.atk + iv.def + iv.sta) / 45) * 100;
  return pct.toFixed(1);
}

/**
 * Convert an ISO yyyy-mm-dd into the modal's "Mon d, yyyy" display
 * format. Returns null on unparseable input.
 */
export function formatFirstCaught(iso: string | null): string | null {
  if (!iso) return null;
  const parts = iso.split("-");
  if (parts.length !== 3) return null;
  const [y, m, d] = parts.map((p) => parseInt(p, 10));
  if (!Number.isFinite(y) || !Number.isFinite(m) || !Number.isFinite(d)) return null;
  const month = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"][m - 1];
  if (!month) return null;
  return `${month} ${d}, ${y}`;
}
